import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';

interface FormFieldProps {
  label: ReactNode;
  htmlFor?: string;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactElement<{
    id?: string;
    'aria-describedby'?: string;
    'aria-invalid'?: boolean | 'true' | 'false';
    'aria-required'?: boolean | 'true' | 'false';
    required?: boolean;
  }>;
}

export function FormField({ label, htmlFor, help, error, required, className = '', children }: FormFieldProps) {
  const generatedId = useId();
  const childProps = isValidElement(children) ? children.props : {};
  const fieldId = htmlFor ?? childProps.id ?? generatedId;
  const helpId = help ? `${fieldId}-help` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [childProps['aria-describedby'], helpId, errorId].filter(Boolean).join(' ') || undefined;

  const child = isValidElement(children)
    ? cloneElement(children, {
        id: fieldId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : childProps['aria-invalid'],
        'aria-required': required ? true : childProps['aria-required'],
        required: required ?? childProps.required,
      })
    : children;

  return (
    <div className={className}>
      <label htmlFor={fieldId} className="block text-sm font-medium text-surface-700 mb-1">
        {label}
        {required && (
          <span className="text-danger-600 ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {child}
      {error && (
        <p id={errorId} className="mt-1 text-sm text-danger-600">
          {error}
        </p>
      )}
      {help && !error && (
        <p id={helpId} className="mt-1 text-sm text-surface-500">
          {help}
        </p>
      )}
    </div>
  );
}
